import { KlausurService } from "@/lib/klausur/klausur.service";
import InputPromptModal from "@/components/inputPromptModal.component";
import type {
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import type { MusterloesungDTO } from "@/features/korrektur/korrektur.types";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { useKlausurCore } from "./klausurCoreProvider";
import * as StateHelpers from "./klausurStateHelpers";

/**
 * Context value for CRUD operations on klausur components.
 * Provides methods to create, update, and delete aufgabenstellungen,
 * sachverhalte, musterlösungen, and korrekturbogen items.
 */
interface KlausurCrudContextValue {
	/** Open modal to create a new Aufgabenstellung */
	openCreateAufgabenstellungModal: () => void;
	/** Open modal to create a new Sachverhalt for an Aufgabenstellung */
	openCreateSachverhaltModal: (aufgabenstellungId: string) => void;
	/** Update the order of sachverhalte within an aufgabenstellung */
	updateSachverhalteIndexes: (
		aufgabenstellungId: string,
		newList: NavigationSachverhaltDTO[],
	) => Promise<void>;
	/** Create a new musterloesung for a sachverhalt; returns created id */
	createMusterloesung: (
		sachverhaltId: string,
		title?: string,
	) => Promise<string>;
	/** Update a musterloesung's title */
	updateMusterloesungTitle: (
		musterloesungId: string,
		title: string,
	) => Promise<void>;
	/** Update an aufgabenstellung's title */
	updateAufgabenstellungTitle: (
		aufgabenstellungId: string,
		title: string,
	) => Promise<void>;
	/** Update a sachverhalt's title */
	updateSachverhaltTitle: (
		sachverhaltId: string,
		title: string,
	) => Promise<void>;
	/** Delete an aufgabenstellung */
	deleteAufgabenstellung: (aufgabenstellungId: string) => Promise<void>;
	/** Delete a sachverhalt under a given aufgabenstellung */
	deleteSachverhalt: (
		aufgabenstellungId: string,
		sachverhaltId: string,
	) => Promise<void>;
	/** Update a korrekturbogen item's max points */
	updateKorrekturbogenItemMaxPoints: (
		korrekturbogenItemId: string,
		maxPoints: number,
	) => Promise<void>;
	/** Update a korrekturbogen item's text */
	updateKorrekturbogenItemText: (
		korrekturbogenItemId: string,
		text: string,
	) => Promise<void>;
	/** Create a new korrekturbogen item within a musterloesung; returns created id */
	createKorrekturbogenItem: (
		musterloesung: MusterloesungDTO,
		title?: string,
	) => Promise<string>;
	/** UI helpers for in-flight creations */
	isCreatingMusterloesung: (sachverhaltId: string) => boolean;
	isCreatingItem: (musterloesungId: string) => boolean;
}

/**
 * Context for CRUD operations on klausur components.
 * Builds on top of KlausurCoreProvider to add mutation capabilities.
 */
const KlausurCrudContext = createContext<KlausurCrudContextValue | undefined>(
	undefined,
);

interface KlausurCrudProviderProps {
	children: React.ReactNode;
}

/**
 * Provider for CRUD operations on klausur components.
 * Wraps KlausurCoreProvider and adds create/update/delete functionality.
 * Manages optimistic updates, server synchronization, and UI state for modals.
 */
export function KlausurCrudProvider({ children }: KlausurCrudProviderProps) {
	const core = useKlausurCore();
	const {
		klausurId,
		organizationId,
		navigationKlausur,
		data,
		reloadKlausur,
		setNavigationKlausur,
		setData,
	} = core;

	// Track in-flight creations for UX (disable buttons, show loading states)
	const [creatingMusterloesungIds, setCreatingMusterloesungIds] = useState<
		Set<string>
	>(new Set());
	const [creatingItemIds, setCreatingItemIds] = useState<Set<string>>(
		new Set(),
	);

	// Modal state for creating aufgabenstellungen and sachverhalte
	const [isPromptOpen, setIsPromptOpen] = useState(false);
	const [isPromptLoading, setIsPromptLoading] = useState(false);
	const [promptMode, setPromptMode] = useState<
		"aufgabe" | "sachverhalt" | null
	>(null);
	const [promptParentAufgabeId, setPromptParentAufgabeId] = useState<
		string | null
	>(null);

	/* ==================== MODAL HANDLERS ==================== */

	const openCreateAufgabenstellungModal = useCallback(() => {
		setPromptMode("aufgabe");
		setPromptParentAufgabeId(null);
		setIsPromptOpen(true);
	}, []);

	const openCreateSachverhaltModal = useCallback(
		(aufgabenstellungId: string) => {
			setPromptMode("sachverhalt");
			setPromptParentAufgabeId(aufgabenstellungId);
			setIsPromptOpen(true);
		},
		[],
	);

	const handleModalClose = useCallback(() => {
		if (isPromptLoading) return;
		setIsPromptOpen(false);
		setPromptMode(null);
		setPromptParentAufgabeId(null);
	}, [isPromptLoading]);

	/**
	 * Dispatches a custom event to notify the selection context of a new selection.
	 * Used to auto-select and scroll to newly created items.
	 */
	const dispatchSelectionEvent = useCallback(
		(detail: { aufgabenstellungId?: string; sachverhaltId?: string }) => {
			document.dispatchEvent(
				new CustomEvent("klausur-selection:set", { detail }),
			);
		},
		[],
	);

	/**
	 * Handles modal confirmation for creating aufgabenstellungen or sachverhalte.
	 * Performs server mutation, optimistic update, and triggers selection event.
	 */
	const handleModalConfirm = useCallback(
		async (title: string) => {
			if (!organizationId || !klausurId || !navigationKlausur) return;
			setIsPromptLoading(true);
			try {
				const service = KlausurService.getInstance();

				if (promptMode === "aufgabe") {
					// Create aufgabenstellung
					const res = await service.createAufgabenstellung(
						organizationId,
						klausurId,
						{ title },
					);
					const newAufgabe = {
						id: res.id,
						title: res.title,
						index: res.index,
						sachverhalte: [],
					};
					const next: NavigationKlausurDTO = {
						...navigationKlausur,
						aufgabenstellungen: [
							...navigationKlausur.aufgabenstellungen,
							newAufgabe,
						],
					};
					setNavigationKlausur(next);

					// Notify selection and reload
					dispatchSelectionEvent({ aufgabenstellungId: res.id });
					await reloadKlausur();

					// Re-dispatch after reload to ensure DOM is ready for scrolling
					setTimeout(() => {
						dispatchSelectionEvent({ aufgabenstellungId: res.id });
					}, 0);
				} else if (promptMode === "sachverhalt" && promptParentAufgabeId) {
					// Create sachverhalt
					const res = await service.createSachverhalt(
						organizationId,
						klausurId,
						promptParentAufgabeId,
						{ title },
					);
					const next: NavigationKlausurDTO = {
						...navigationKlausur,
						aufgabenstellungen: navigationKlausur.aufgabenstellungen.map((a) =>
							a.id === promptParentAufgabeId
								? {
										...a,
										sachverhalte: [
											...a.sachverhalte,
											{ id: res.id, title: res.title, index: res.index },
										],
									}
								: a,
						),
					};
					setNavigationKlausur(next);

					// Notify selection and reload
					dispatchSelectionEvent({
						aufgabenstellungId: promptParentAufgabeId,
						sachverhaltId: res.id,
					});
					await reloadKlausur();

					// Re-dispatch after reload
					setTimeout(() => {
						dispatchSelectionEvent({
							aufgabenstellungId: promptParentAufgabeId,
							sachverhaltId: res.id,
						});
					}, 0);
				}

				// Close modal
				setIsPromptOpen(false);
				setPromptMode(null);
				setPromptParentAufgabeId(null);
			} finally {
				setIsPromptLoading(false);
			}
		},
		[
			organizationId,
			klausurId,
			navigationKlausur,
			promptMode,
			promptParentAufgabeId,
			reloadKlausur,
			setNavigationKlausur,
			dispatchSelectionEvent,
		],
	);

	/* ==================== CREATE OPERATIONS ==================== */

	/**
	 * Creates a new musterloesung under a given sachverhalt.
	 * Performs optimistic update and tracks in-flight state.
	 * Returns the id of the created musterloesung.
	 */
	const createMusterloesung = useCallback(
		async (sachverhaltId: string, title?: string) => {
			if (!organizationId || !klausurId) return "";
			const loading = new Set(creatingMusterloesungIds);
			loading.add(sachverhaltId);
			setCreatingMusterloesungIds(loading);

			try {
				const payloadTitle = (title ?? "Musterlösung").trim() || "Musterlösung";
				const resp = await KlausurService.getInstance().createMusterloesung(
					organizationId,
					klausurId,
					sachverhaltId,
					{ title: payloadTitle },
				);

				// Optimistically add to local state
				const newMusterloesung: MusterloesungDTO = {
					id: resp.id,
					korrekturMusterloesungId: resp.id,
					index: resp.index,
					title: resp.title ?? payloadTitle,
					userPoints: null,
					maxPoints: 0,
					korrekturbogenItems: [],
				};
				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.addMusterloesungToData(
						prev,
						sachverhaltId,
						newMusterloesung,
					);
				});

				return resp.id;
			} finally {
				const done = new Set(creatingMusterloesungIds);
				done.delete(sachverhaltId);
				setCreatingMusterloesungIds(done);
			}
		},
		[organizationId, klausurId, creatingMusterloesungIds, setData],
	);

	/**
	 * Creates a new korrekturbogen item inside a musterloesung.
	 * Performs optimistic update and tracks in-flight state.
	 * Returns the id of the created item.
	 */
	const createKorrekturbogenItem = useCallback(
		async (musterloesung: MusterloesungDTO, title?: string) => {
			if (!organizationId || !klausurId) return "";
			const musterloesungId =
				musterloesung.korrekturMusterloesungId || musterloesung.id;
			const loading = new Set(creatingItemIds);
			loading.add(musterloesungId);
			setCreatingItemIds(loading);

			try {
				const payloadTitle =
					(title ?? "Korrekturbogen-Item").trim() || "Korrekturbogen-Item";
				const resp =
					await KlausurService.getInstance().createKorrekturbogenItem(
						organizationId,
						klausurId,
						musterloesungId,
						{ text: payloadTitle, maxPoints: 0 },
					);

				// Optimistically add to local state
				const newItem = {
					id: resp.id,
					index: resp.index,
					text: payloadTitle,
					maxPoints: 0,
					userPoints: 0,
				};
				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.addKorrekturbogenItemToData(
						prev,
						musterloesungId,
						newItem,
					);
				});

				return resp.id;
			} finally {
				const done = new Set(creatingItemIds);
				done.delete(musterloesungId);
				setCreatingItemIds(done);
			}
		},
		[organizationId, klausurId, creatingItemIds, setData],
	);

	/* ==================== UPDATE OPERATIONS ==================== */

	/**
	 * Updates an aufgabenstellung's title.
	 * Performs optimistic update on both data and navigation state.
	 */
	const updateAufgabenstellungTitle = useCallback(
		async (aufgabenstellungId: string, title: string) => {
			if (!organizationId || !klausurId) return;
			try {
				await KlausurService.getInstance().updateAufgabenstellung(
					organizationId,
					klausurId,
					aufgabenstellungId,
					{ title },
				);

				// Update detailed editor data
				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateAufgabenstellungTitleInData(
						prev,
						aufgabenstellungId,
						title,
					);
				});

				// Update navigation data
				setNavigationKlausur((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateAufgabenstellungTitleInNav(
						prev,
						aufgabenstellungId,
						title,
					);
				});
			} catch (e) {
				console.error(e);
			}
		},
		[organizationId, klausurId, setData, setNavigationKlausur],
	);

	/**
	 * Updates a sachverhalt's title.
	 * Performs optimistic update on both data and navigation state.
	 */
	const updateSachverhaltTitle = useCallback(
		async (sachverhaltId: string, title: string) => {
			if (!organizationId || !klausurId) return;
			try {
				await KlausurService.getInstance().updateSachverhalt(
					organizationId,
					klausurId,
					sachverhaltId,
					{ title },
				);

				// Update detailed editor data
				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateSachverhaltTitleInData(
						prev,
						sachverhaltId,
						title,
					);
				});

				// Update navigation data
				setNavigationKlausur((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateSachverhaltTitleInNav(
						prev,
						sachverhaltId,
						title,
					);
				});
			} catch (e) {
				console.error(e);
			}
		},
		[organizationId, klausurId, setData, setNavigationKlausur],
	);

	/**
	 * Updates a musterloesung's title.
	 * Performs optimistic update on data state.
	 */
	const updateMusterloesungTitle = useCallback(
		async (musterloesungId: string, title: string) => {
			if (!organizationId || !klausurId) return;
			try {
				await KlausurService.getInstance().updateMusterloesung(
					organizationId,
					klausurId,
					musterloesungId,
					{ title },
				);

				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateMusterloesungTitleInData(
						prev,
						musterloesungId,
						title,
					);
				});
			} catch (e) {
				console.error(e);
			}
		},
		[organizationId, klausurId, setData],
	);

	/**
	 * Updates a korrekturbogen item's max points.
	 * Keeps text unchanged; fetches current text before update.
	 */
	const updateKorrekturbogenItemMaxPoints = useCallback(
		async (korrekturbogenItemId: string, maxPoints: number) => {
			if (!organizationId || !klausurId) return;
			const currentText = StateHelpers.findKorrekturbogenItemText(
				data,
				korrekturbogenItemId,
			);

			try {
				await KlausurService.getInstance().updateKorrekturbogenItem(
					organizationId,
					klausurId,
					korrekturbogenItemId,
					{ text: currentText, maxPoints },
				);

				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateKorrekturbogenItemMaxPointsInData(
						prev,
						korrekturbogenItemId,
						maxPoints,
					);
				});
			} catch (e) {
				console.error(e);
			}
		},
		[organizationId, klausurId, data, setData],
	);

	/**
	 * Updates a korrekturbogen item's text.
	 * Keeps maxPoints unchanged; fetches current maxPoints before update.
	 */
	const updateKorrekturbogenItemText = useCallback(
		async (korrekturbogenItemId: string, text: string) => {
			if (!organizationId || !klausurId) return;
			const currentMaxPoints = StateHelpers.findKorrekturbogenItemMaxPoints(
				data,
				korrekturbogenItemId,
			);

			try {
				await KlausurService.getInstance().updateKorrekturbogenItem(
					organizationId,
					klausurId,
					korrekturbogenItemId,
					{ text, maxPoints: currentMaxPoints },
				);

				setData((prev) => {
					if (!prev) return prev;
					return StateHelpers.updateKorrekturbogenItemTextInData(
						prev,
						korrekturbogenItemId,
						text,
					);
				});
			} catch (e) {
				console.error(e);
			}
		},
		[organizationId, klausurId, data, setData],
	);

	/**
	 * Updates the order of sachverhalte within an aufgabenstellung.
	 * Performs optimistic update and syncs to server.
	 */
	const updateSachverhalteIndexes = useCallback(
		async (aufgabenstellungId: string, newList: NavigationSachverhaltDTO[]) => {
			if (!organizationId || !klausurId) return;

			// Optimistically update provider navigation state
			setNavigationKlausur((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					aufgabenstellungen: prev.aufgabenstellungen.map((a) =>
						a.id === aufgabenstellungId ? { ...a, sachverhalte: newList } : a,
					),
				};
			});

			try {
				await KlausurService.getInstance().bulkUpdateSachverhalteIndexes(
					organizationId,
					klausurId,
					newList.map((sv) => ({ id: sv.id, index: sv.index })),
				);
				await reloadKlausur();
			} catch (err) {
				console.error("Failed to persist sachverhalte indexes", err);
				// Reload to restore server state on error
				await reloadKlausur();
			}
		},
		[organizationId, klausurId, reloadKlausur, setNavigationKlausur],
	);

	/* ==================== DELETE OPERATIONS ==================== */

	/**
	 * Deletes an aufgabenstellung.
	 * Performs optimistic removal and reloads on completion.
	 */
	const deleteAufgabenstellung = useCallback(
		async (aufgabenstellungId: string) => {
			if (!organizationId || !klausurId) return;

			// Optimistic local remove
			setData((prev) => {
				if (!prev) return prev;
				return StateHelpers.removeAufgabenstellungFromData(
					prev,
					aufgabenstellungId,
				);
			});
			setNavigationKlausur((prev) => {
				if (!prev) return prev;
				return StateHelpers.removeAufgabenstellungFromNav(
					prev,
					aufgabenstellungId,
				);
			});

			try {
				await KlausurService.getInstance().deleteComponent(
					organizationId,
					klausurId,
					"aufgabenstellung",
					klausurId,
					aufgabenstellungId,
				);
				await reloadKlausur();
			} catch (e) {
				console.error(e);
				// Reload to restore server state on error
				await reloadKlausur();
			}
		},
		[organizationId, klausurId, reloadKlausur, setData, setNavigationKlausur],
	);

	/**
	 * Deletes a sachverhalt under a given aufgabenstellung.
	 * Performs optimistic removal and reloads on completion.
	 */
	const deleteSachverhalt = useCallback(
		async (aufgabenstellungId: string, sachverhaltId: string) => {
			if (!organizationId || !klausurId) return;

			// Optimistic local remove
			setData((prev) => {
				if (!prev) return prev;
				return StateHelpers.removeSachverhaltFromData(
					prev,
					aufgabenstellungId,
					sachverhaltId,
				);
			});
			setNavigationKlausur((prev) => {
				if (!prev) return prev;
				return StateHelpers.removeSachverhaltFromNav(
					prev,
					aufgabenstellungId,
					sachverhaltId,
				);
			});

			try {
				await KlausurService.getInstance().deleteComponent(
					organizationId,
					klausurId,
					"sachverhalt",
					aufgabenstellungId,
					sachverhaltId,
				);
				await reloadKlausur();
			} catch (e) {
				console.error(e);
				// Reload to restore server state on error
				await reloadKlausur();
			}
		},
		[organizationId, klausurId, reloadKlausur, setData, setNavigationKlausur],
	);

	/* ==================== CONTEXT VALUE ==================== */

	const value = useMemo(
		() => ({
			openCreateAufgabenstellungModal,
			openCreateSachverhaltModal,
			updateSachverhalteIndexes,
			createMusterloesung,
			updateMusterloesungTitle,
			updateAufgabenstellungTitle,
			updateSachverhaltTitle,
			deleteAufgabenstellung,
			deleteSachverhalt,
			updateKorrekturbogenItemMaxPoints,
			updateKorrekturbogenItemText,
			createKorrekturbogenItem,
			isCreatingMusterloesung: (sachverhaltId: string) =>
				creatingMusterloesungIds.has(sachverhaltId),
			isCreatingItem: (musterloesungId: string) =>
				creatingItemIds.has(musterloesungId),
		}),
		[
			openCreateAufgabenstellungModal,
			openCreateSachverhaltModal,
			updateSachverhalteIndexes,
			createMusterloesung,
			updateMusterloesungTitle,
			updateAufgabenstellungTitle,
			updateSachverhaltTitle,
			deleteAufgabenstellung,
			deleteSachverhalt,
			updateKorrekturbogenItemMaxPoints,
			updateKorrekturbogenItemText,
			createKorrekturbogenItem,
			creatingMusterloesungIds,
			creatingItemIds,
		],
	);

	return (
		<KlausurCrudContext.Provider value={value}>
			{children}
			<InputPromptModal
				open={isPromptOpen}
				title={
					promptMode === "sachverhalt"
						? "Neuen Sachverhalt erstellen"
						: "Neue Aufgabenstellung erstellen"
				}
				description={
					promptMode === "sachverhalt"
						? "Bitte geben Sie den Titel des Sachverhalts ein."
						: "Bitte geben Sie den Titel der Aufgabenstellung ein."
				}
				placeholder={
					promptMode === "sachverhalt"
						? "Sachverhaltstitel"
						: "Aufgabenstellungstitel"
				}
				loading={isPromptLoading}
				onClose={handleModalClose}
				onConfirm={handleModalConfirm}
			/>
		</KlausurCrudContext.Provider>
	);
}

/**
 * Hook to access CRUD operations context.
 * Must be used within KlausurCrudProvider.
 */
export function useKlausurCrud() {
	const ctx = useContext(KlausurCrudContext);
	if (!ctx)
		throw new Error("useKlausurCrud must be used within KlausurCrudProvider");
	return ctx;
}
